import { Controller } from "../../ts/controller";
import { Logger } from "../../ts/utils/logger";
import { MinMaxBounds } from "../../ts/utils/min-max-bounds";

class ControlBounds {
    static fontSize = new MinMaxBounds(8, 24);
    static lineHeight = new MinMaxBounds(0.5, 2);
    static horizontalPadding = new MinMaxBounds(0, 15);
}

class ControlValues {
    /** Percent */
    horizontalPadding: number = 5;
    /** px */
    fontSize: number = 16;
    lineHeight: number = 1;
    bold: boolean = false;

    public toJsonString(): string {
        return JSON.stringify({
            horizontalPadding: this.horizontalPadding,
            fontSize: this.fontSize,
            lineHeight: this.lineHeight,
            bold: this.bold,
        });
    }

    static fromJsonString(jsonString: string): ControlValues {
        const instance = new ControlValues();
        try {
            const parsed = JSON.parse(jsonString);

            if (typeof parsed.horizontalPadding === 'number') {
                instance.horizontalPadding = parsed.horizontalPadding;
            } else if (parsed.horizontalPadding !== undefined) {
                Logger.warn(
                    'ControlValues',
                    `Type mismatch for property "horizontalPadding". Expected number, got ${typeof parsed.horizontalPadding}.`
                );
            }

            if (typeof parsed.fontSize === 'number') {
                instance.fontSize = parsed.fontSize;
            } else if (parsed.fontSize !== undefined) {
                Logger.warn(
                    'ControlValues',
                    `Type mismatch for property "fontSize". Expected number, got ${typeof parsed.fontSize}.`
                );
            }

            if (typeof parsed.lineHeight === 'number') {
                instance.lineHeight = parsed.lineHeight;
            } else if (parsed.lineHeight !== undefined) {
                Logger.warn(
                    'ControlValues',
                    `Type mismatch for property "lineHeight". Expected number, got ${typeof parsed.lineHeight}.`
                );
            }

            if (typeof parsed.bold === 'boolean') {
                instance.bold = parsed.bold;
            } else if (parsed.bold !== undefined) {
                Logger.warn(
                    'ControlValues',
                    `Type mismatch for property "bold". Expected boolean, got ${typeof parsed.bold}.`
                );
            }
        } catch (e) {
            Logger.warn(
                'ControlValues',
                `Failed to parse ControlValues from JSON string. Input: "${jsonString}", Error: ${e}`
            );
        }
        return instance;
    }
}

export class TabTextViewController extends Controller {
    controlValues: ControlValues | null = null;

    constructor() {
        super({
            logLabel: 'TabTextViewController',
        });
    }

    public initialize(): void {
        this.controlValues = new ControlValues();
        this._pullSettingsFromStorage();
        this._bindControls();
        this._setState();
        Logger.info(this.logLabel, 'Initialized');
    }

    private _pullSettingsFromStorage(): void {
        const settings = localStorage.getItem("tab-text-view-control");
        if (settings) {
            this.controlValues = ControlValues.fromJsonString(settings);
        }
    }

    private _pushSettingsToStorage(): void {
        const settings = this.controlValues!.toJsonString();
        localStorage.setItem("tab-text-view-control", settings);
    }

    private _bindControls(): void {
        const addPaddingButton = this.getElementByIdOrWarn("tab-text-view-add-padding-button");
        const reducePaddingButton = this.getElementByIdOrWarn("tab-text-view-reduce-padding-button");
        const addFontSizeButton = this.getElementByIdOrWarn("tab-text-view-add-font-size-button");
        const reduceFontSizeButton = this.getElementByIdOrWarn("tab-text-view-reduce-font-size-button");
        const addLineHeightButton = this.getElementByIdOrWarn("tab-text-view-add-line-height-button");
        const reduceLineHeightButton = this.getElementByIdOrWarn("tab-text-view-reduce-line-height-button");
        const toggleBoldButton = this.getElementByIdOrWarn("tab-text-view-toggle-bold-button");

        if (!addPaddingButton || !reducePaddingButton || !addFontSizeButton ||
            !reduceFontSizeButton || !addLineHeightButton || !reduceLineHeightButton || !toggleBoldButton) return;

        addPaddingButton.addEventListener("click", () => {
            this.controlValues!.horizontalPadding = ControlBounds.horizontalPadding.clamp(
                this.controlValues!.horizontalPadding + 1
            );
            this._setState();
            this._pushSettingsToStorage();
        });

        reducePaddingButton.addEventListener("click", () => {
            this.controlValues!.horizontalPadding = ControlBounds.horizontalPadding.clamp(
                this.controlValues!.horizontalPadding - 1
            );
            this._setState();
            this._pushSettingsToStorage();
        });

        addFontSizeButton.addEventListener("click", () => {
            this.controlValues!.fontSize = ControlBounds.fontSize.clamp(
                this.controlValues!.fontSize + 1
            );
            this._setState();
            this._pushSettingsToStorage();
        });

        reduceFontSizeButton.addEventListener("click", () => {
            this.controlValues!.fontSize = ControlBounds.fontSize.clamp(
                this.controlValues!.fontSize - 1
            );
            this._setState();
            this._pushSettingsToStorage();
        });

        addLineHeightButton.addEventListener("click", () => {
            this.controlValues!.lineHeight = ControlBounds.lineHeight.clamp(
                this.controlValues!.lineHeight + 0.1
            );
            this._setState();
            this._pushSettingsToStorage();
        });

        reduceLineHeightButton.addEventListener("click", () => {
            this.controlValues!.lineHeight = ControlBounds.lineHeight.clamp(
                this.controlValues!.lineHeight - 0.1
            );
            this._setState();
            this._pushSettingsToStorage();
        });

        toggleBoldButton.addEventListener("click", () => {
            this.controlValues!.bold = !this.controlValues!.bold;
            this._setState();
            this._pushSettingsToStorage();
        });
    }

    private _setState(): void {
        const textElement = this.getElementByIdOrWarn("tab-text-view-text");
        if (!textElement) return;

        const containerWidth = textElement.parentElement!.clientWidth;
        const paddingPixels = (this.controlValues!.horizontalPadding / 100) * containerWidth;

        textElement.style.paddingLeft = `${paddingPixels}px`;
        textElement.style.paddingRight = `${paddingPixels}px`;
        textElement.style.fontSize = `${this.controlValues!.fontSize}px`;
        textElement.style.lineHeight = `${this.controlValues!.lineHeight}`;
        textElement.classList.toggle('bold', this.controlValues!.bold);
    }
}
